<script lang="ts">
	import Pause from '@lucide/svelte/icons/pause'
	import Play from '@lucide/svelte/icons/play'
	import RotateCcw from '@lucide/svelte/icons/rotate-ccw'
	import { useSearchParams } from 'runed/kit'
	import { onMount } from 'svelte'

	import { resolve } from '$app/paths'
	import ComboBox from '#lib/components/combo-box.svelte'
	import CtaButton from '#lib/components/cta-button.svelte'
	import MetricPicker from '#lib/components/metric-picker.svelte'
	import { Button } from '#lib/components/ui/button/index.js'
	import { CHAINS_CONFIG } from '#lib/constants/chainsConfig.js'
	import {
		getChainIdsFromSlugs,
		getChainOptions,
		getShortChainName,
		getSlugsFromChainIds,
	} from '#lib/utils/chainsHelpers.js'
	import { formatElapsed } from '#lib/utils/formatters.js'
	import { phg } from '#lib/utils/posthog.js'
	import { TIME_CHART_METRICS, timeChartSchema } from '#lib/utils/schema.js'
	import { StopWatch } from '#lib/utils/stopwatch.svelte.js'
	import { TIME_CHART_METRIC_CONFIG } from '#lib/utils/timeChartMetrics.js'

	let { data } = $props()
	const params = useSearchParams(timeChartSchema, { noScroll: true })

	const urlChains = $derived(getChainIdsFromSlugs(params.chains))
	const chains = $derived(urlChains.length > 0 ? urlChains : data.chains.map(({ id }) => id))

	const stopwatch = new StopWatch()

	onMount(() => {
		const timeout = setTimeout(() => stopwatch.start(), 100)

		return () => {
			clearTimeout(timeout)
			stopwatch.reset()
		}
	})

	// 0s times (ex. "Instant") still fill their bar, over this long
	const INSTANT_FILL_MS = 200

	const metric = $derived(TIME_CHART_METRIC_CONFIG[params.metric])
</script>

{#snippet controls()}
	<Button aria-label="Reset" onclick={() => stopwatch.reset()} variant="outline">
		<RotateCcw size="16" />
	</Button>

	<Button
		aria-label={stopwatch.isRunning ? 'Pause' : 'Play'}
		onclick={() => stopwatch.toggle()}
		variant="outline"
	>
		{#if stopwatch.isRunning}
			<Pause size="16" />
		{:else}
			<Play size="16" />
		{/if}
	</Button>
{/snippet}

<section class="container max-w-245 px-2 py-3 sm:px-6 sm:py-16">
	<CtaButton class="mb-5 w-full font-extrabold sm:mb-8" size="xl" />

	<div class="bg-card text-card-foreground rounded-lg border py-1 shadow-sm sm:py-4">
		<!-- One set of controls: under the timer on phones, beside the pickers from xs up -->
		<div
			class="bg-card xs:gap-2 sticky top-0 grid w-full grid-cols-[1fr_auto] gap-3 px-3 py-2 sm:px-5 sm:py-3"
		>
			<div class="xs:row-span-1 row-span-2 flex flex-col items-start justify-between">
				<h1 class="xs:text-3xl mb-2 text-2xl font-bold tracking-tight sm:text-4xl">
					{metric.name()}
				</h1>

				<p class="text-muted-foreground mb-1.5 text-sm">
					Data from
					<span class="text-branding-gradient font-bold">Chainspect</span>
				</p>
			</div>

			<p
				class="text-foreground xs:text-5xl text-right text-2xl font-light tabular-nums lg:text-6xl"
			>
				{formatElapsed(stopwatch.elapsed)}
			</p>

			<div class="col-start-2 flex items-center justify-end gap-2">
				{@render controls()}
			</div>

			<div
				class="xs:col-span-1 xs:col-start-1 xs:row-start-2 xs:justify-start xs:w-auto xs:justify-self-start col-span-2 flex w-full flex-wrap items-center justify-between gap-3"
			>
				<MetricPicker
					align="center"
					metrics={TIME_CHART_METRICS.map(value => ({
						label: TIME_CHART_METRIC_CONFIG[value].name(),
						value,
					}))}
					onValueChange={value => {
						stopwatch.reset()

						phg.capture('time_chart_metric_change', { value })
					}}
					bind:metric={params.metric}
				/>

				<ComboBox
					class="xs:w-44 w-1/2 flex-1"
					onChange={() => stopwatch.reset()}
					options={getChainOptions(data.chains.map(({ id }) => CHAINS_CONFIG[id]))}
					placeholder="Select networks"
					bind:value={
						() => chains,
						value => {
							// At least one chain stays picked: an empty pick would mean all chains again
							if (Array.isArray(value) && value.length > 0)
								params.chains = getSlugsFromChainIds(value)
						}
					}
				/>
			</div>
		</div>

		<!-- Grid columns: chain (as wide as the longest name), bar, time -->
		<div
			class="mt-3 grid w-full grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-6 px-3 sm:mt-6 sm:px-5"
		>
			{#each data.chains.filter(c => chains.includes(c.id)) as c (c.id)}
				{const chain = $derived(CHAINS_CONFIG[c.id])}
				{const progress = $derived(
					(stopwatch.elapsed * 100) / (c.value === 0 ? INSTANT_FILL_MS : c.value * 1000)
				)}

				<div class="contents">
					<a
						class="flex items-center justify-end text-xs sm:gap-2"
						href={resolve('/(app)/chain/[slug]', {
							slug: chain.slug,
						})}
					>
						<span class="hidden whitespace-nowrap sm:inline">
							{getShortChainName(chain.name)}
						</span>

						<img
							class="size-7 min-h-7 min-w-7"
							alt="{chain.name} logo"
							crossorigin="anonymous"
							decoding="async"
							fetchpriority="low"
							height="28"
							loading="lazy"
							src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{chain.slug}.svg"
							width="28"
						/>
					</a>

					<div class="bg-muted flex h-2.5 w-full rounded-full">
						<!-- eslint-disable svelte/no-inline-styles -->

						<div
							style:width="{Math.min(progress, 100)}%"
							class="h-2.5 rounded-full transition-[width] duration-200 ease-linear"
							class:bg-branding-blue={progress < 100}
							class:bg-green-500={progress >= 100}
						></div>
					</div>

					<p
						class="text-muted-foreground xs:text-sm xs:w-18 w-15 shrink-0 text-left text-xs tabular-nums"
					>
						{metric.format(c.value)}
					</p>
				</div>
			{/each}
		</div>
	</div>
</section>
